import React, { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { day, qty } from '../lib/format.js';
import { ROLES, can } from '../lib/roles.js';
import { Alert, Card, CardSkeleton, Empty, Field, Pill, useAsync } from '../components/ui.jsx';

const VIEWS = [
  { key: 'pending', label: 'Needs properties' },
  { key: 'recorded', label: 'Recorded' },
  { key: '', label: 'All batches' },
];

/**
 * Chemical properties live here because Tally has nowhere to keep them. Batches
 * themselves arrive from the Tally purchase and stock sync.
 */
export default function Batches() {
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const entityId = session.entities[0] && session.entities[0].id;
  const specStatus = params.get('spec_status') ?? '';
  const search = params.get('search') ?? '';
  const godownName = params.get('godown') || '';
  const godown = session.godowns.find((g) => g.name === godownName);
  // ?expiring=30 comes from the expiry alert. The server has no such filter, so
  // the in-stock batches are fetched (they arrive soonest-expiry first) and cut here.
  const expiringDays = Number.parseInt(params.get('expiring') || '', 10);
  const expiring = Number.isFinite(expiringDays) && expiringDays > 0 ? expiringDays : null;
  const canRecord = can(session, ROLES.RECORD_SPEC);

  const query = new URLSearchParams();
  if (entityId) query.set('entity_id', entityId);
  if (specStatus) query.set('spec_status', specStatus);
  if (search) query.set('search', search);
  if (godown) query.set('godown_id', godown.id);
  if (expiring) { query.set('in_stock', 'true'); query.set('limit', '500'); }
  const list = useAsync(() => api.get(`/api/batches?${query.toString()}`).then((res) => {
    if (!expiring) return res;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const until = new Date(today); until.setDate(until.getDate() + expiring);
    return {
      ...res,
      batches: res.batches.filter((b) => {
        if (!b.expiry_date) return false;
        const d = new Date(b.expiry_date);
        return d >= today && d <= until;
      }),
    };
  }), [entityId, specStatus, search, godownName, expiring]);

  const source = useAsync(() => (entityId ? api.get(`/api/batches/stock-source?entity_id=${entityId}`) : Promise.resolve(null)), [entityId]);
  const [open, setOpen] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const setFilter = (patch) => {
    const next = { ...Object.fromEntries(params), ...patch };
    Object.keys(next).forEach((k) => !next[k] && delete next[k]);
    setParams(next);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Batches &amp; specifications</h1>
          <div className="page-sub">
            Every batch that can be sold needs its chemical properties on record — they print on the invoice for
            exactly the batches despatched, and an invoice will not issue without them.
          </div>
        </div>
        <div className="inline">
          <div className="segmented" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button key={v.label} aria-pressed={specStatus === v.key}
                onClick={() => setFilter({ spec_status: v.key })}>{v.label}</button>
            ))}
          </div>
          {canRecord && (
            <button className="btn" onClick={() => setImportOpen(!importOpen)}>
              <span aria-hidden="true">⇪</span> Import from Excel
            </button>
          )}
        </div>
      </div>

      {(search || godownName || expiring) && (
        <div className="filter-chips">
          {expiring && <span className="chip">In stock, expiring within {expiring} days<button aria-label="Clear" onClick={() => setFilter({ expiring: '' })}>×</button></span>}
          {godownName && <span className="chip">Godown: {godownName}<button aria-label="Clear" onClick={() => setFilter({ godown: '' })}>×</button></span>}
          {search && <span className="chip">“{search}”<button aria-label="Clear" onClick={() => setFilter({ search: '' })}>×</button></span>}
        </div>
      )}

      {importOpen && canRecord && <ImportPanel entityId={entityId} onImported={list.reload} onClose={() => setImportOpen(false)} />}

      <div className="toolbar">
        <Field label="Search batch or item">
          <input defaultValue={search} placeholder="IPA/2609…"
            onKeyDown={(e) => e.key === 'Enter' && setFilter({ search: e.currentTarget.value })}
            onBlur={(e) => setFilter({ search: e.target.value })} />
        </Field>
        <Field label="Godown">
          <select value={godownName} onChange={(e) => setFilter({ godown: e.target.value })}>
            <option value="">All godowns</option>
            {session.godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </Field>
      </div>

      {source.data && source.data.item_level && (
        <Alert kind="warn">
          Tally's stock is being read as item totals only, with no batch or godown, so all of it is shown
          under {source.data.godown || 'one godown'}. Load InvIT's batch stock TDL (InvITBatchStock.tdl) in Tally on
          the Connector computer to see each batch in its own godown.
        </Alert>
      )}

      <Alert onRetry={list.reload}>{list.error}</Alert>
      {list.loading && !list.data && <CardSkeleton lines={6} />}

      {list.data && (
        <Card pad={list.data.batches.length === 0}>
          {list.data.batches.length === 0 ? (
            <Empty title="No batches match">
              Batches are created by the Tally purchase and stock sync — a batch Tally does not know about cannot be sold.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead>
                  <tr><th>Item</th><th>Batch</th><th>Purchased</th><th>Expiry</th>
                    <th className="r">In Tally</th><th className="r">Available</th><th>Properties</th><th>TC</th><th /></tr>
                </thead>
                <tbody>
                  {list.data.batches.map((b) => (
                    <tr key={b.id} className="clickable" onClick={() => setOpen(open === b.id ? null : b.id)}>
                      <td>{b.item_name}</td>
                      <td className="mono">{b.batch_name}</td>
                      <td>{day(b.purchase_date)}</td>
                      <td>{day(b.expiry_date)}</td>
                      <td className="r num">{qty(b.qty_tally, b.uom)}</td>
                      <td className="r num"><b>{qty(b.qty_available)}</b></td>
                      <td><Pill value={b.spec_status} /></td>
                      <td>{b.batch_name === 'Primary Batch' ? <span className="muted">—</span>
                        : b.has_tc ? <span className="pill ok">filed</span> : <span className="pill warn">missing</span>}</td>
                      <td className="r muted" aria-hidden="true">{open === b.id ? '▴' : '▾'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Keyed so a newly opened batch never starts with the last one's values. */}
      {open && <SpecEditor key={open} batchId={open} canRecord={canRecord}
        canRelease={can(session, ROLES.ADMIN_ACCOUNTS)} onSaved={list.reload} />}
    </>
  );
}

function ImportPanel({ entityId, onImported, onClose }) {
  const groups = useAsync(() => api.get('/api/item-groups'), []);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [groupId, setGroupId] = useState('');

  const upload = async (file, dryRun) => {
    setError(null); setResult(null); setBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await api.post(`/api/batches/import?entity_id=${entityId}&dry_run=${dryRun}`, { file_base64: base64 });
      setResult(res);
      if (!dryRun) onImported();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    if (!groupId) return;
    let res;
    try {
      res = await api.raw(`/api/batches/import-template?entity_id=${entityId}&item_group_id=${groupId}`);
    } catch {
      setError('Could not build the template — is a specification template defined for this group?'); return;
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = 'invit-batch-specifications.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card title="Bulk entry from the lab's workbook"
      note="One row per batch, one column per parameter. Validate first — nothing is written until you import."
      actions={<button className="btn small ghost" onClick={onClose}>Close</button>}>
      <Alert>{error}</Alert>
      <div className="row">
        <Field label="1 · Download a template" hint="columns and specification bands for an item group">
          <div className="inline">
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} style={{ flex: 1 }}>
              <option value="">Select item group…</option>
              {groups.data && groups.data.item_groups.map((g) => (
                <option key={g.id} value={g.id} disabled={!g.template_id}>
                  {g.name}{g.template_id ? '' : ' — no template yet'}
                </option>
              ))}
            </select>
            <button className="btn secondary" disabled={!groupId} onClick={downloadTemplate}>Download</button>
          </div>
        </Field>
        <Field label="2 · Check the filled workbook" hint="reports every problem, writes nothing">
          <input ref={fileRef} type="file" accept=".xlsx" disabled={busy}
            onChange={(e) => e.target.files[0] && upload(e.target.files[0], true)} />
        </Field>
        <Field label="3 · Import" hint="applies the values to the batches">
          <input type="file" accept=".xlsx" disabled={busy}
            onChange={(e) => e.target.files[0] && upload(e.target.files[0], false)} />
        </Field>
      </div>

      {result && (
        <>
          <Alert kind={result.failed ? 'warn' : 'ok'}>
            {result.dry_run ? 'Validation' : 'Import'}: {result.imported} of {result.total} rows accepted
            {result.failed ? `, ${result.failed} rejected — fix these rows and run it again.` : '.'}
          </Alert>
          {result.rows.filter((r) => r.status === 'error').length > 0 && (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Row</th><th>Item</th><th>Batch</th><th>Problem</th></tr></thead>
                <tbody>
                  {result.rows.filter((r) => r.status === 'error').map((r) => (
                    <tr key={r.row}><td className="num">{r.row}</td><td>{r.item}</td>
                      <td className="mono">{r.batch}</td><td>{r.message}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function SpecEditor({ batchId, canRecord, canRelease, onSaved }) {
  const spec = useAsync(() => api.get(`/api/batches/${batchId}/spec`), [batchId]);
  const [values, setValues] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (spec.data) setValues(Object.fromEntries(spec.data.parameters.map((p) => [p.code, p.value ?? ''])));
  }, [spec.data]);

  if (spec.loading && !spec.data) return <CardSkeleton lines={3} />;
  if (spec.error) return <Alert onRetry={spec.reload}>{spec.error}</Alert>;
  if (!spec.data || !values) return null;
  if (!spec.data.template) {
    return (
      <Card title="Chemical properties">
        <Alert kind="warn">
          No specification template applies to this item. Create one for its item group first — it defines the
          parameters, their units and their specification bands.
        </Alert>
      </Card>
    );
  }

  const save = async () => {
    setError(null); setSaved(false);
    try {
      await api.put(`/api/batches/${batchId}/spec`, { values });
      setSaved(true); spec.reload(); onSaved();
    } catch (e) { setError(e.message); }
  };

  const outOfSpec = (p, v) => {
    if (p.data_type !== 'number' || v === '' || v === null) return false;
    const n = Number(v);
    return (p.min_value !== null && n < Number(p.min_value)) || (p.max_value !== null && n > Number(p.max_value));
  };
  const filled = Object.values(values).filter((v) => v !== '' && v !== null).length;

  const batch = spec.data.batch;
  const release = async (action, remarks) => {
    setError(null); setSaved(false);
    try {
      await api.post(`/api/batches/${batchId}/qc/${action}`, remarks ? { remarks } : {});
      spec.reload(); onSaved();
    } catch (e) { setError(e.details && e.details.missing ? `${e.message}` : e.message); }
  };

  return (
    <Card title={`${batch.batch_name} — ${spec.data.template.name}`}
      note={`${filled} of ${spec.data.parameters.length} parameters recorded${batch.coa_number ? ` · ${batch.coa_number}` : ''}`}
      actions={(
        <>
          <button className="btn small secondary"
            onClick={() => { setError(null); api.open(`/api/batches/${batchId}/coa?format=pdf`).catch((e) => setError(e.message)); }}>Certificate</button>
          <button className="btn small ghost"
            onClick={() => { setError(null); api.open(`/api/batches/${batchId}/label`).catch((e) => setError(e.message)); }}>Drum label</button>
          {canRelease && batch.spec_status !== 'approved' && !batch.qc_rejected && (
            <button className="btn small secondary" onClick={() => release('approve')}>Release (QC)</button>
          )}
          {canRelease && !batch.qc_rejected && (
            <button className="btn small ghost" onClick={() => {
              const why = window.prompt('Why is this batch being rejected?');
              if (why) release('reject', why);
            }}>Reject</button>
          )}
          {canRecord && <button className="btn small" onClick={save}>Save properties</button>}
        </>
      )}>
      <Alert>{error}</Alert>
      {saved && <Alert kind="ok">Saved. This batch can now go on an invoice.</Alert>}
      {batch.qc_rejected && (
        <Alert kind="error">
          Rejected by quality control{batch.qc_remarks ? ` — ${batch.qc_remarks}` : ''}. It will not leave the godown.
        </Alert>
      )}
      {batch.spec_status === 'approved' && !batch.qc_rejected && (
        <Alert kind="ok">
          Released by quality control{batch.qc_approved_at ? ` on ${day(batch.qc_approved_at)}` : ''}
          {batch.coa_number ? ` · certificate ${batch.coa_number}` : ''}.
        </Alert>
      )}
      <div className="row">
        {spec.data.parameters.map((p) => (
          <Field key={p.id}
            label={`${p.label}${p.uom ? ` (${p.uom})` : ''}${p.is_required ? ' *' : ''}`}
            hint={[p.min_value !== null ? `min ${p.min_value}` : '', p.max_value !== null ? `max ${p.max_value}` : '', p.test_method]
              .filter(Boolean).join(' · ') || undefined}>
            <input value={values[p.code] ?? ''} disabled={!canRecord} inputMode={p.data_type === 'number' ? 'decimal' : 'text'}
              onChange={(e) => setValues({ ...values, [p.code]: e.target.value })}
              style={outOfSpec(p, values[p.code])
                ? { borderColor: 'var(--critical)', boxShadow: '0 0 0 3px color-mix(in srgb, var(--critical) 14%, transparent)' }
                : undefined} />
            {outOfSpec(p, values[p.code]) && <div className="sub" style={{ color: 'var(--critical-text)' }}>Outside the specification band</div>}
          </Field>
        ))}
      </div>
      <div className="card-note">A value outside its band is flagged here and printed in red on the invoice.</div>
    </Card>
  );
}
